import { useCallback, useContext, useEffect, useState } from 'react'
import { toZonedTime } from 'date-fns-tz'

import { AuthenticationContext } from '../../providers/auth-provider'
import Api from '../api'

import useDates from './useDates'

export const getPausedState = (device: IDeviceType) => {
  const { mute, pauseStartDate, pauseEndDate } = device.node || {}

  const isPausedIndefinitely = mute
  const now = new Date()
  const startDate = new Date(pauseStartDate)
  const endDate = new Date(pauseEndDate)
  const isPaused = (startDate < now && endDate > now) || isPausedIndefinitely
  const willBePaused = startDate > now

  return {
    now,
    startDate,
    endDate,
    isPaused,
    willBePaused,
    isPausedIndefinitely,
  }
}

const usePauseDevice = ({ device, timezone }: { device: IDeviceType | undefined; timezone?: string }) => {
  const folderId = device?.node.folderId
  const { ownUser } = useContext(AuthenticationContext)
  const [folderTimezone, setFolderTimezone] = useState<string | undefined>(timezone)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  let { pauseStartDate, pauseEndDate } = device?.node || {}
  if (!pauseStartDate) pauseStartDate = new Date(0).toISOString()
  if (!pauseEndDate) pauseEndDate = new Date(0).toISOString()
  const { formatDate, formatTime, timezoneAbbr, globalTimezone } = useDates(folderTimezone || ownUser?.information?.timezone)

  const fetchFolder = useCallback(async () => {
    if (typeof folderTimezone === 'undefined' && folderId) {
      const res = await Api.get(`/api/folders/${folderId}`)
      if (res.ok) {
        setFolderTimezone(res.body.timezone)
      }
    }
  }, [folderId, folderTimezone])

  const onRefresh = useCallback(async () => {
    setIsLoading(true)
    await fetchFolder()
    setIsLoading(false)
  }, [fetchFolder])

  useEffect(() => {
    onRefresh()
  }, [onRefresh])

  if (!device) return {}
  const { now, startDate, endDate, isPaused, willBePaused, isPausedIndefinitely } = getPausedState(device)
  const endDateInTimezone = toZonedTime(new Date(pauseEndDate), globalTimezone)
  const startDateInTimezone = toZonedTime(new Date(pauseStartDate), globalTimezone)
  const endsAtMidnight = endDateInTimezone.getHours() === 23 && endDateInTimezone.getMinutes() === 59
  const startsAtMidnight = startDateInTimezone.getHours() === 0 && startDateInTimezone.getMinutes() === 0

  let pausedText = ''

  // Is paused indefinitely
  if (isPausedIndefinitely) {
    pausedText = 'This device is paused.'
  } else if (startDate && endDate) {
    // Currently paused, only show when pause will end
    if (startDate < now && endDate > now) {
      const formattedEndDate = `${formatDate(pauseEndDate)} at ${formatTime(pauseEndDate)}`
      pausedText = `Paused until ${formattedEndDate} - ${timezoneAbbr}`
    }
    // Will be paused, show start and end of pause
    if (startDate > now && endDate > now) {
      if (endsAtMidnight) {
        const formattedStartDate = startsAtMidnight ? formatDate(pauseStartDate) : `${formatDate(pauseStartDate)} at ${formatTime(pauseStartDate)}`
        const formattedEndDate = formatDate(pauseEndDate)
        pausedText = `Paused from ${formattedStartDate} through the end of ${formattedEndDate} - ${timezoneAbbr}`
      } else {
        const formattedStartDate = `${formatDate(pauseStartDate)} at ${formatTime(pauseStartDate)}`
        const formattedEndDate = `${formatDate(pauseEndDate)} at ${formatTime(pauseEndDate)}`
        pausedText = `Paused from ${formattedStartDate} until ${formattedEndDate} - ${timezoneAbbr}`
      }
    }
  }

  return {
    isPaused,
    willBePaused,
    isPausedIndefinitely,
    pausedText,
    folderTimezone,
    timezoneAbbr,
    selectedTimezone: globalTimezone,
    pauseStartDate,
    pauseEndDate,
    onRefresh,
    isLoading,
  }
}

export default usePauseDevice
